import { PreviewContainer } from 'modules/templateEditor/common';
import { CSSProperties } from 'react';
import styled from 'styled-components';
import {
  caption,
  colors,
  fonts,
  fontSizes,
  fontWeights,
  letterSpacing,
  shadows,
  spacing,
  subtleShadow,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { OS, PreviewType } from '../../common/types';

const notificationBackground = `radial-gradient(circle, transparent, rgba(252, 172, 209, 0.2) 100%), radial-gradient(circle, transparent, rgba(181, 109, 198, 0.3) 100%)`;

const textShadows = {
  extraLarge: [
    `0 0 ${pxToRem(1)} rgba(0, 0, 0, 0.04)`,
    `0 0 ${pxToRem(2)} rgba(0, 0, 0, 0.06)`,
    `${pxToRem(1)} ${pxToRem(1)} ${pxToRem(7)} rgba(0, 0, 0, 0.15)`,
  ],
  medium: [
    `0 0 ${pxToRem(1)} rgba(0, 0, 0, 0.04)`,
    `0 0 ${pxToRem(2)} rgba(0, 0, 0, 0.06)`,
    `0 ${pxToRem(1)} ${pxToRem(3)} rgba(0, 0, 0, 0.15)`,
  ],
};

export const SMSPreviewContainer = styled(PreviewContainer)`
  /**
   * This needs to be wrapped in styled because it's 
   * used to apply additional styles to PreviewContainer 
   * in other modules.
   */
`;

export const containerStyles: CSSProperties = {
  alignItems: `center`,
  display: `flex`,
  height: `100%`,
  justifyContent: `center`,
};

export const deviceStylesCommon: CSSProperties = {
  alignItems: `center`,
  borderRadius: pxToRem(20),
  display: `flex`,
  flexDirection: `column`,
  height: pxToRem(534),
  letterSpacing: letterSpacing.pos10,
  margin: `auto`,
  overflow: `hidden`,
  width: pxToRem(286),
};

export const deviceStyles = {
  [OS.android]: {
    [PreviewType.message]: {
      backgroundColor: colors.white,
      borderColor: `transparent`,
      // note: outline is being applied here as box-shadow because Safari doesn't apply border-radius to outline
      boxShadow: [...shadows.subtle, `0 0 0 ${spacing.eighth} ${colors.grey25}`].join(', '),
      fontFamily: fonts.roboto,
    } as CSSProperties,
    [PreviewType.notification]: {
      alignItems: `center`,
      backgroundColor: colors.purple10,
      backgroundImage: notificationBackground,
      // note: outline is being applied here as box-shadow because Safari doesn't apply border-radius to outline
      boxShadow: [...shadows.subtle, `0 0 0 ${spacing.eighth} rgba(181, 109, 198, 0.2)`].join(', '),
      fontFamily: fonts.roboto,
    } as CSSProperties,
    [PreviewType.contactCard]: {
      position: 'relative',
      backgroundColor: colors.grey1,
      borderColor: `transparent`,
      // note: outline is being applied here as box-shadow because Safari doesn't apply border-radius to outline
      boxShadow: [...shadows.subtle, `0 0 0 ${spacing.eighth} ${colors.grey25}`].join(', '),
      fontFamily: fonts.sfPro,
    } as CSSProperties,
  },
  [OS.ios]: {
    [PreviewType.message]: {
      backgroundColor: colors.white,
      borderColor: `transparent`,
      // note: outline is being applied here as box-shadow because Safari doesn't apply border-radius to outline
      boxShadow: [...shadows.subtle, `0 0 0 ${spacing.eighth} ${colors.grey25}`].join(', '),
      fontFamily: fonts.sfPro,
    } as CSSProperties,
    [PreviewType.notification]: {
      backgroundColor: colors.purple10,
      backgroundImage: notificationBackground,
      // note: outline is being applied here as box-shadow because Safari doesn't apply border-radius to outline
      boxShadow: [...shadows.subtle, `0 0 0 ${spacing.eighth} rgba(181, 109, 198, 0.2)`].join(', '),
      fontFamily: fonts.sfPro,
    } as CSSProperties,
    [PreviewType.contactCard]: {
      position: 'relative',
      backgroundColor: colors.grey1,
      borderColor: `transparent`,
      // note: outline is being applied here as box-shadow because Safari doesn't apply border-radius to outline
      boxShadow: [...shadows.subtle, `0 0 0 ${spacing.eighth} ${colors.grey25}`].join(', '),
      fontFamily: fonts.sfPro,
    } as CSSProperties,
  },
};

export const deviceStylesUnset: CSSProperties = {
  backgroundColor: colors.white,
  // note: outline is being applied here as box-shadow because Safari doesn't apply border-radius to outline
  boxShadow: [...shadows.subtle, `0 0 0 ${spacing.eighth} ${colors.grey25}`].join(', '),
  justifyContent: `center`,
  textAlign: `center`,
};

export const messageUnset: CSSProperties = {
  ...caption,
  color: colors.textSecondary,
  margin: `0 ${spacing.plusHalf}`,
};

export const rotate180: CSSProperties = {
  transform: `rotate(180deg)`,
};

// Message Specific

export const messageBody: CSSProperties = {
  alignItems: `flex-start`,
  boxSizing: `border-box`,
  display: `flex`,
  flex: `1 1 auto`,
  flexDirection: `column`,
  fontWeight: fontWeights.regular,
  overflowX: `hidden`,
  overflowY: `auto`,
  padding: pxToRem(14),
  paddingBottom: pxToRem(42),
  width: `100%`,
};

export const messageHeaderAndroid: CSSProperties = {
  alignItems: `center`,
  backgroundColor: colors.white,
  boxSizing: `border-box`,
  display: `flex`,
  flex: `0 0 auto`,
  height: pxToRem(60),
  paddingTop: pxToRem(8),
  width: `100%`,
  ...subtleShadow,
};

export const messageHeaderAndroidSide: CSSProperties = {
  display: `flex`,
  flex: `0 0 ${pxToRem(48)}`,
  justifyContent: `center`,
};

export const messageHeaderAndroidCenter: CSSProperties = {
  display: `flex`,
  flex: `1 1 auto`,
  justifyContent: `center`,
};

export const messageHeaderIos: CSSProperties = {
  alignItems: `center`,
  backgroundColor: `#F6F6F6`,
  borderBottom: `${spacing.single} solid #E5E5E5`,
  boxSizing: `border-box`,
  display: `flex`,
  flex: `0 0 auto`,
  height: pxToRem(85),
  paddingTop: pxToRem(7),
  width: `100%`,
};

export const messageHeaderIosSide: CSSProperties = {
  display: `flex`,
  flex: `0 0 ${pxToRem(48)}`,
  justifyContent: `center`,
};

export const messageHeaderIosCenter: CSSProperties = {
  display: `flex`,
  flex: `1 1 auto`,
  justifyContent: `center`,
};

export const previewInfoMessage: CSSProperties = {
  display: `flex`,
  gap: spacing.half,
  alignItems: `center`,
  justifyContent: `center`,
  margin: `${spacing.plusHalf}`,

  ...caption,
  color: colors.textSecondary,
};

export const messageFooterAndroid: CSSProperties = {
  alignItems: `center`,
  backgroundColor: colors.white,
  borderTop: `${spacing.single} solid #E5E5E5`,
  boxSizing: `border-box`,
  columnGap: pxToRem(7),
  display: `flex`,
  flex: `0 0 auto`,
  fontWeight: fontWeights.regular,
  height: pxToRem(57),
  padding: `0 ${pxToRem(18)} ${pxToRem(3)} ${pxToRem(22)}`,
  width: `100%`,
};

export const messageFooterAndroidInput: CSSProperties = {
  color: colors.grey60,
  flex: `1 1 auto`,
  fontSize: pxToRem(12),
};

export const messageFooterAndroidSend: CSSProperties = {
  alignItems: `center`,
  backgroundColor: `#35B8C0`,
  borderRadius: pxToRem(14),
  display: `flex`,
  flex: `0 0 auto`,
  height: pxToRem(41),
  justifyContent: `center`,
  width: pxToRem(41),
};

export const messageFooterIos: CSSProperties = {
  backgroundColor: `#D4D7DF`,
  flex: `0 0 auto`,
  fontWeight: fontWeights.regular,
  height: pxToRem(36),
  overflow: `visible`,
  position: `relative`,
  width: `100%`,
};

export const messageBodyAndroidDate: CSSProperties = {
  color: colors.grey60,
  flex: `0 0 auto`,
  fontSize: pxToRem(13),
  textAlign: `center`,
  width: `100%`,
};

export const messageBodyIosDateTime: CSSProperties = {
  color: colors.black,
  flex: `0 0 auto`,
  fontSize: pxToRem(10),
  textAlign: `center`,
  textTransform: `capitalize`,
  width: `100%`,
};

export const messageBodyMedia: CSSProperties = {
  backgroundSize: `cover`,
  borderRadius: pxToRem(7),
  flex: `0 0 auto`,
  height: pxToRem(151),
  marginTop: pxToRem(7),
  width: pxToRem(151),
};

export const messageBodyAndroidMessageContainer: CSSProperties = {
  alignItems: `flex-end`,
  columnGap: pxToRem(7),
  display: `flex`,
  flex: `0 0 auto`,
  lineHeight: 1.4,
  marginTop: pxToRem(7),
};

export const messageBodyAndroidMessage: CSSProperties = {
  backgroundColor: colors.grey5,
  borderRadius: pxToRem(4),
  color: colors.black,
  flex: `0 0 auto`,
  fontSize: pxToRem(12),
  maxWidth: pxToRem(197),
  overflowWrap: `break-word`,
  padding: pxToRem(8),
  whiteSpace: `pre-wrap`,
};

export const messageBodyAndroidMessageTime: CSSProperties = {
  color: colors.grey60,
  flex: `0 0 auto`,
  fontSize: pxToRem(10),
  paddingBottom: pxToRem(9),
};

export const messageBodyIosMessage: CSSProperties = {
  backgroundColor: `#E9E8EB`,
  borderRadius: pxToRem(7),
  color: colors.black,
  flex: `0 0 auto`,
  fontSize: pxToRem(12),
  lineHeight: 1.3,
  marginTop: pxToRem(7),
  maxWidth: pxToRem(206),
  overflowWrap: `break-word`,
  padding: pxToRem(6),
  whiteSpace: `pre-wrap`,
};

export const messageInputIos: CSSProperties = {
  alignItems: `center`,
  backgroundColor: colors.white,
  border: `${spacing.single} solid #E5E5E5`,
  borderRadius: pxToRem(14),
  bottom: `calc(100% + ${pxToRem(6)})`,
  boxSizing: `border-box`,
  color: colors.textDisabled,
  display: `flex`,
  fontSize: pxToRem(11),
  height: pxToRem(29),
  left: `50%`,
  padding: `0 ${pxToRem(14)}`,
  position: `absolute`,
  transform: `translateX(-50%)`,
  width: pxToRem(237),
};

// Notification Specific

export const notificationTimeCommon: CSSProperties = {
  color: colors.white,
  fontSize: pxToRem(70),
  letterSpacing: letterSpacing.pos10,
  lineHeight: 1,
  textAlign: `center`,
  textShadow: `${textShadows.extraLarge.join(', ')}`,
};

export const notificationTimeOS = {
  [OS.android]: {
    fontWeight: fontWeights.regular,
    marginTop: pxToRem(66),
  } as CSSProperties,
  [OS.ios]: {
    fontWeight: fontWeights.light,
    marginTop: pxToRem(60),
  } as CSSProperties,
};

export const notificationDate: CSSProperties = {
  color: colors.white,
  fontSize: pxToRem(18),
  fontWeight: fontWeights.regular,
  letterSpacing: letterSpacing.pos10,
  textAlign: `center`,
  textShadow: `${textShadows.medium.join(', ')}`,
};

export const notificationBubble = {
  [OS.android]: {
    backgroundColor: colors.white,
    borderRadius: pxToRem(2),
    boxSizing: `border-box`,
    color: colors.black,
    display: `flex`,
    flexDirection: `column`,
    marginTop: pxToRem(15),
    padding: `${pxToRem(15)} ${pxToRem(13)} ${pxToRem(10)} ${pxToRem(9)}`,
    width: pxToRem(280),
  } as CSSProperties,
  [OS.ios]: {
    backdropFilter: `blur(${pxToRem(50)})`,
    backgroundColor: `rgba(255, 255, 255, 0.7)`,
    borderRadius: pxToRem(9),
    boxSizing: `border-box`,
    display: `flex`,
    marginTop: pxToRem(16),
    padding: `${pxToRem(8)} ${pxToRem(11)} ${pxToRem(7)} ${pxToRem(6)}`,
    width: pxToRem(278),
  } as CSSProperties,
};

export const notificationBubbleAndroidHeader: CSSProperties = {
  alignItems: `center`,
  display: `flex`,
  width: pxToRem(258),
};

export const messengerIcon: CSSProperties = {
  marginRight: pxToRem(6),
};

export const notificationBubbleAndroidHeaderTime: CSSProperties = {
  alignItems: `center`,
  display: `flex`,
  flex: `1 1 auto`,
  fontSize: pxToRem(10),
  justifyContent: `flex-start`,
};

export const notificationBubbleAndroidBody: CSSProperties = {
  display: `flex`,
  marginTop: pxToRem(9),
  width: pxToRem(258),
};

export const notificationBubbleAndroidBodyContent: CSSProperties = {
  display: `flex`,
  flex: `1 1 auto`,
  flexDirection: `column`,
  width: pxToRem(166),
};

export const notificationBubbleAndroidBodyContentSender: CSSProperties = {
  fontSize: pxToRem(11),
  fontWeight: fontWeights.medium,
  overflow: `hidden`,
  textOverflow: `ellipsis`,
  whiteSpace: `nowrap`,
};

export const notificationBubbleAndroidBodyContentMsg: CSSProperties = {
  alignItems: `flex-start`,
  display: `flex`,
  fontSize: pxToRem(12),
  lineHeight: 1.4,
  marginTop: pxToRem(5),
};

export const notificationBubbleAndroidBodyContentMsgTxt: CSSProperties = {
  flex: `1 1 auto`,
  overflow: `hidden`,
  overflowWrap: `break-word`,
  whiteSpace: `pre-wrap`,
};

export const notificationBubbleAndroidBodyContentMsgMedia: CSSProperties = {
  flex: `0 0 auto`,
  backgroundSize: `cover`,
  borderRadius: pxToRem(2),
  height: pxToRem(28),
  marginLeft: pxToRem(5),
  width: pxToRem(28),
};

export const notificationBubbleAvatar = {
  [OS.android]: {
    flex: `0 0 ${pxToRem(44)}`,
  } as CSSProperties,
  [OS.ios]: {
    flex: `0 0 ${pxToRem(38)}`,
  } as CSSProperties,
};

export const avatarAndroid: CSSProperties = {
  alignItems: `center`,
  backgroundColor: `#3147AB`,
  borderRadius: `50%`,
  display: `flex`,
  height: pxToRem(32),
  justifyContent: `center`,
  width: pxToRem(32),
};

export const avatarIos: CSSProperties = {
  backgroundImage: `linear-gradient(#A5AAB7, #858794)`,
  borderRadius: `50%`,
  height: pxToRem(28),
  marginTop: pxToRem(6),
  position: 'relative',
  width: pxToRem(28),
};

export const avatarIosBadge: CSSProperties = {
  bottom: `0`,
  position: `absolute`,
  right: pxToRem(-3),
};

export const avatarIos24: CSSProperties = {
  backgroundImage: `linear-gradient(#A5AAB7, #858794)`,
  borderRadius: `50%`,
  height: pxToRem(34),
  width: pxToRem(34),
};

export const notificationBubbleIosContent: CSSProperties = {
  flex: `0 0 auto`,
  width: pxToRem(223),
};

export const notificationBubbleIosContentHeader: CSSProperties = {
  alignItems: `flex-start`,
  display: `flex`,
  width: `inherit`,
};

export const notificationBubbleIosContentHeaderSender: CSSProperties = {
  color: colors.textPrimary,
  flex: `1 1 auto`,
  fontSize: pxToRem(12),
  fontWeight: fontWeights.semiBold,
  overflow: `hidden`,
  textOverflow: `ellipsis`,
  whiteSpace: `nowrap`,
};

export const notificationBubbleIosContentHeaderTime: CSSProperties = {
  color: colors.textSecondary,
  flex: `0 0 auto`,
  fontSize: pxToRem(10),
  marginLeft: pxToRem(5),
};

export const notificationBubbleIosContentBody: CSSProperties = {
  alignItems: `flex-start`,
  display: `flex`,
  width: `inherit`,
};

export const notificationBubbleIosContentBodyMsg: CSSProperties = {
  color: colors.textPrimary,
  flex: `1 1 auto`,
  fontSize: pxToRem(12),
  lineHeight: 1.3,
  overflow: `hidden`,
  overflowWrap: `break-word`,
  whiteSpace: `pre-wrap`,
};

export const notificationBubbleIosContentBodyMedia: CSSProperties = {
  flex: `0 0 auto`,
  backgroundSize: `cover`,
  borderRadius: pxToRem(2),
  height: pxToRem(28),
  marginLeft: pxToRem(5),
  width: pxToRem(28),
};

// Contact card styles

export const messageContactCardIosMessage: CSSProperties = {
  backgroundColor: `#E9E8EB`,
  borderRadius: pxToRem(7),
  marginTop: pxToRem(7),
  color: colors.black,
  flex: `0 0 auto`,
  fontSize: pxToRem(12),
  lineHeight: 1.3,
  maxWidth: pxToRem(206),
  minWidth: pxToRem(160),
  overflowWrap: `break-word`,
  padding: pxToRem(6),
  whiteSpace: `pre-wrap`,
  display: 'flex',
  alignItems: 'center',
};

export const messageContactCardIosDisplayNameText: CSSProperties = {
  width: '90%',
  marginTop: pxToRem(12),
  marginBottom: pxToRem(12),
  marginLeft: pxToRem(10),
  marginRight: pxToRem(10),
  fontWeight: fontWeights.semiBold,
  flexGrow: 1,
};

export const messageContactCardAndroidMessage: CSSProperties = {
  alignItems: `flex-end`,
  columnGap: pxToRem(7),
  display: `flex`,
  flex: `0 0 auto`,
  lineHeight: 1.4,
  minWidth: pxToRem(140),
};

export const messageContactCardAndroidDisplayNameText: CSSProperties = {
  margin: pxToRem(8),
  fontWeight: fontWeights.semiBold,
  flexGrow: 1,
};

export const messageContactCardAvatarImage: CSSProperties = {
  height: pxToRem(32),
  width: pxToRem(32),
  marginRight: pxToRem(4),
  borderRadius: '24px',
};

export const notificationContactCardIosContentBodyMsg: CSSProperties = {
  color: colors.textPrimary,
  flex: `1 1 auto`,
  fontSize: pxToRem(12),
  lineHeight: 1.3,
  overflow: `hidden`,
  overflowWrap: `break-word`,
  whiteSpace: `pre-wrap`,
  marginTop: pxToRem(8),
};

export const notificationContactCardIosAvatarImage: CSSProperties = {
  height: pxToRem(24),
  width: pxToRem(24),
  borderRadius: '24px',
  marginTop: pxToRem(8),
};

export const notificationContactCardAndroidAvatarImage: CSSProperties = {
  height: pxToRem(24),
  width: pxToRem(24),
  borderRadius: '24px',
  marginTop: pxToRem(-8),
};

export const contactCardIosBackIcon: CSSProperties = {
  position: 'absolute',
  left: pxToRem(20),
  top: pxToRem(30),
};

export const contactCardHeader: CSSProperties = {
  height: pxToRem(180),
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

export const contactCardIosAvatarImage: CSSProperties = {
  height: pxToRem(64),
  width: pxToRem(64),
  borderRadius: '36px',
  marginTop: pxToRem(36),
};

export const contactCardIosDisplayName: CSSProperties = {
  marginTop: pxToRem(14),
  fontSize: fontSizes.s18,
  fontWeight: fontWeights.bold,
  textAlign: 'center',
  maxWidth: 260,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
};

export const contactCardIosSubheader: CSSProperties = {
  marginTop: pxToRem(4),
  fontSize: fontSizes.s12,
  textAlign: 'center',
};

export const contactCardDetailSection: CSSProperties = {
  minHeight: pxToRem(54),
  width: '100%',
  marginTop: pxToRem(10),
  paddingTop: pxToRem(12),
  paddingLeft: pxToRem(24),
  backgroundColor: colors.white,
};

export const contactCardDetailName: CSSProperties = {
  fontSize: fontSizes.s12,
};

export const contactCardDetailValue: CSSProperties = {
  fontSize: fontSizes.s16,
  fontWeight: fontWeights.bold,
  marginTop: pxToRem(6),
  overflowX: 'hidden',
  textOverflow: 'ellipsis',
};

export const contactCardDetailValueDisabled: CSSProperties = {
  ...contactCardDetailValue,
  color: colors.textDisabled,
};

export const flexDirectionColumn: CSSProperties = {
  flexDirection: 'column',
};

export const linkText: CSSProperties = {
  color: `${colors.link}`,
  textDecoration: 'none',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  alignSelf: 'stretch',
  whiteSpace: 'pre',
};
